import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowTrendingUpIcon, 
  BellIcon,
  CreditCardIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { Trend, ApiUsage, CustomAlert, ViralPrediction, MomentumAnalysis, BusinessInsights, RealtimeData, User } from '../types';
import toast from 'react-hot-toast';

// Dashboard Layouts by Tier
const DashboardLayouts = {
  starter: {
    columns: 2,
    widgets: ['trend_overview', 'quick_stats', 'recent_alerts', 'api_usage', 'subscription_management']
  },
  professional: {
    columns: 3,
    widgets: ['trend_overview', 'viral_predictions', 'momentum_analysis', 'trend_comparison', 'alert_manager', 'api_usage', 'subscription_management']
  },
  business: {
    columns: 4,
    widgets: ['trend_overview', 'business_insights', 'forecasting', 'audience_analysis', 'custom_reports', 'alert_manager', 'api_usage', 'market_opportunities', 'subscription_management']
  },
  enterprise: {
    columns: 6,
    widgets: ['trend_overview', 'business_insights', 'forecasting', 'audience_analysis', 'custom_reports', 'alert_manager', 'api_usage', 'market_opportunities', 'realtime_streams', 'custom_metrics', 'model_performance', 'data_sources']
  }
};

// Widget Components
const TrendOverviewWidget: React.FC<{ trends: Trend[] }> = ({ trends }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Trending Now</h3>
    <div className="space-y-3">
      {trends.slice(0, 5).map((trend) => (
        <div key={trend.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">{trend.trend_name}</p>
            <p className="text-sm text-gray-500">{trend.category}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{trend.total_engagement.toLocaleString()}</p>
                         <div className="flex items-center text-xs text-gray-500">
               <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
               {trend.momentum_score}%
             </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const QuickStatsWidget: React.FC<{ stats: any }> = ({ stats }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
    <div className="grid grid-cols-2 gap-4">
      <div className="text-center">
        <p className="text-2xl font-bold text-purple-600">{stats.activeTrends}</p>
        <p className="text-sm text-gray-500">Active Trends</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-blue-600">{stats.totalEngagement}</p>
        <p className="text-sm text-gray-500">Total Engagement</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-green-600">{stats.alerts}</p>
        <p className="text-sm text-gray-500">Active Alerts</p>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-orange-600">{stats.reports}</p>
        <p className="text-sm text-gray-500">Reports</p>
      </div>
    </div>
  </div>
);

const RecentAlertsWidget: React.FC<{ alerts: CustomAlert[] }> = ({ alerts }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Alerts</h3>
    <div className="space-y-3">
      {alerts.slice(0, 5).map((alert) => (
        <div key={alert.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
          <BellIcon className="h-5 w-5 text-red-500 mr-3" />
          <div className="flex-1">
            <p className="font-medium text-gray-900">{alert.name}</p>
            <p className="text-sm text-gray-500">{alert.alert_type}</p>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${
            alert.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {alert.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      ))}
    </div>
  </div>
);


const ApiUsageWidget: React.FC<{ usage: ApiUsage }> = ({ usage }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">API Usage</h3>
    <div className="space-y-4">
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span>This Month</span>
          <span>{usage.requests_this_month.toLocaleString()} / {usage.quotas.requests_per_month.toLocaleString()}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full" 
            style={{ width: `${(usage.requests_this_month / usage.quotas.requests_per_month) * 100}%` }}
          ></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500">Today</p>
          <p className="font-medium">{usage.requests_today.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500">Remaining</p>
          <p className="font-medium">{usage.rate_limit_remaining.toLocaleString()}</p>
        </div>
      </div>
    </div>
  </div>
);

const ViralPredictionsWidget: React.FC<{ predictions: ViralPrediction[] }> = ({ predictions }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Viral Predictions</h3>
    <div className="space-y-3">
      {predictions.slice(0, 3).map((prediction) => (
        <div key={prediction.trend_name} className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
          <p className="font-medium text-gray-900">{prediction.trend_name}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">Viral Probability</span>
            <span className="text-lg font-bold text-purple-600">
              {(prediction.viral_probability * 100).toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-gray-500">Predicted Reach</span>
            <span className="text-sm font-medium">{prediction.predicted_reach.toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MomentumAnalysisWidget: React.FC<{ analysis: MomentumAnalysis[] }> = ({ analysis }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Momentum Analysis</h3>
    <div className="space-y-3">
      {analysis.slice(0, 3).map((item) => (
        <div key={item.trend_name} className="p-3 bg-gray-50 rounded-lg">
          <p className="font-medium text-gray-900">{item.trend_name}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">Momentum Score</span>
            <span className={`text-lg font-bold ${
              item.momentum_score > 70 ? 'text-green-600' : 
              item.momentum_score > 40 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {item.momentum_score}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-gray-500">Classification</span>
            <span className={`px-2 py-1 text-xs rounded-full ${
              item.classification === 'high' ? 'bg-green-100 text-green-800' :
              item.classification === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {item.classification}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const BusinessInsightsWidget: React.FC<{ insights: BusinessInsights[] }> = ({ insights }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Insights</h3>
    <div className="space-y-3">
      {insights.slice(0, 3).map((insight, index) => (
        <div key={index} className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Market Size</span>
            <span className="text-sm font-bold text-blue-600">
              ${(insight.market_size / 1000000).toFixed(1)}M
            </span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-blue-700">Growth Potential</span>
            <span className="text-sm font-medium text-blue-600">
              {(insight.growth_potential * 100).toFixed(1)}%
            </span>
          </div>
          <div className="text-xs text-blue-600">
            {insight.opportunities.length} opportunities identified
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RealtimeStreamsWidget: React.FC<{ data: RealtimeData[] }> = ({ data }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Real-time Streams</h3>
    <div className="space-y-3">
      {data.slice(0, 3).map((stream, index) => (
        <div key={index} className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Window</span>
            <span className="text-sm text-green-600">{stream.window_size}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-green-700">Alerts Triggered</span>
            <span className="text-sm font-medium text-green-600">{stream.alerts_triggered.length}</span>
          </div>
          <div className="text-xs text-green-600">
            {stream.data_sources.length} active data sources
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { isFeatureEnabled, setLoading, addNotification, loadingStates } = useApp();
  const [trends, setTrends] = useState<Trend[]>([]);
  const [apiUsage, setApiUsage] = useState<ApiUsage | null>(null);
  const [alerts, setAlerts] = useState<CustomAlert[]>([]);
  const [viralPredictions, setViralPredictions] = useState<ViralPrediction[]>([]);
  const [momentumAnalysis, setMomentumAnalysis] = useState<MomentumAnalysis[]>([]);
  const [businessInsights, setBusinessInsights] = useState<BusinessInsights[]>([]);
  const [realtimeData, setRealtimeData] = useState<RealtimeData[]>([]);
  const [stats, setStats] = useState({
    activeTrends: 0,
    totalEngagement: 0,
    alerts: 0,
    reports: 0
  });

  // State for refreshed user data
  const [refreshedUser, setRefreshedUser] = useState<User | null>(null);
  
  // Get plan from refreshed user data if available, otherwise fall back to context user
  const currentUser = refreshedUser || user;
  const userPlan = currentUser?.subscription?.plan_tier || currentUser?.plan || 'free';
  const layout = DashboardLayouts[userPlan as keyof typeof DashboardLayouts] || DashboardLayouts.starter;
  
  // Debug logging
  console.log('🔍 Dashboard - currentUser:', currentUser);
  console.log('🔍 Dashboard - userPlan:', userPlan);
  console.log('🔍 Dashboard - subscription:', currentUser?.subscription);
  console.log('🔍 Dashboard - subscription.plan_tier:', currentUser?.subscription?.plan_tier);
  console.log('🔍 Dashboard - user.plan:', currentUser?.plan);

  useEffect(() => {
    // Don't load data if user is not authenticated
    if (!user) {
      return;
    }

    const loadDashboardData = async () => {
      setLoading('dashboard', true);
      try {
        // Refresh user data to get latest subscription info
        try {
          const refreshedUserData = await apiService.getCurrentUser();
          // Update the refreshed user state
          if (refreshedUserData) {
            console.log('🔍 Refreshed user data:', refreshedUserData);
            console.log('🔍 Refreshed user subscription:', refreshedUserData?.subscription);
            console.log('🔍 Refreshed user subscription type:', typeof refreshedUserData?.subscription);
            console.log('🔍 Refreshed user subscription keys:', refreshedUserData?.subscription ? Object.keys(refreshedUserData.subscription) : 'N/A');
            setRefreshedUser(refreshedUserData);
          }
        } catch (error) {
          console.warn('Failed to refresh user data:', error);
        }

        // Load basic data for all tiers
        const [trendsData, usageData, alertsData] = await Promise.all([
          apiService.getTrends(),
          apiService.getApiUsage(),
          apiService.getAlerts()
        ]);

        setTrends(trendsData.data || []);
        setApiUsage(usageData);
        setAlerts(alertsData);

        // Load tier-specific data
        if (isFeatureEnabled('canAccessAdvancedAnalytics')) {
          const [predictionsData, momentumData] = await Promise.all([
            apiService.getViralPredictions(),
            apiService.getMomentumAnalysis()
          ]);
          // Extract arrays from response objects
          setViralPredictions(predictionsData?.viral_predictions || predictionsData || []);
          setMomentumAnalysis(momentumData?.momentum_analysis || momentumData || []);
        }

        if (isFeatureEnabled('canAccessBusinessIntelligence')) {
          // Load business insights data
          // This would typically come from a different endpoint
          setBusinessInsights([]);
        }

        if (isFeatureEnabled('canAccessRealTimeData')) {
          const realtimeData = await apiService.getRealtimeData();
          setRealtimeData(realtimeData);
        }

        // Calculate stats
        setStats({
          activeTrends: trendsData.data?.length || 0,
          totalEngagement: trendsData.data?.reduce((sum, trend) => sum + trend.total_engagement, 0) || 0,
          alerts: alertsData.length,
          reports: 0 // Would come from reports API
        });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        addNotification({
          id: 'dashboard-error',
          type: 'error',
          title: 'Dashboard Error',
          message: 'Failed to load dashboard data. Please try again.',
          timestamp: new Date().toISOString(),
          read: false,
          category: 'system'
        });
      } finally {
        setLoading('dashboard', false);
      }
    };

    loadDashboardData();
  }, [user, userPlan, isFeatureEnabled, setLoading, addNotification]);

  const renderWidget = (widgetType: string) => {
    // Don't render widgets if data is still loading
    if (loadingStates['dashboard']) {
      return (
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    switch (widgetType) {
      case 'trend_overview':
        return trends && trends.length > 0 ? <TrendOverviewWidget trends={trends} /> : null;
      case 'quick_stats':
        return <QuickStatsWidget stats={stats} />;
      case 'recent_alerts':
        return alerts && alerts.length > 0 ? <RecentAlertsWidget alerts={alerts} /> : null;
      case 'api_usage':
        return apiUsage ? <ApiUsageWidget usage={apiUsage} /> : null;
      case 'subscription_management':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Subscription</h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Current Plan</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">{userPlan}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Billing Cycle</p>
                <p className="text-sm text-gray-900 capitalize">
                  {refreshedUser?.subscription?.billing_cycle || 'monthly'}
                </p>
              </div>
              <div className="pt-2">
                <a
                  href="/billing"
                  className="inline-flex items-center text-sm font-medium text-purple-600 hover:text-purple-700"
                >
                  Manage Billing
                  <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        );
      case 'viral_predictions':
        return isFeatureEnabled('canAccessAdvancedAnalytics') && viralPredictions && viralPredictions.length > 0 ? 
          <ViralPredictionsWidget predictions={viralPredictions} /> : null;
      case 'momentum_analysis':
        return isFeatureEnabled('canAccessAdvancedAnalytics') && momentumAnalysis && momentumAnalysis.length > 0 ? 
          <MomentumAnalysisWidget analysis={momentumAnalysis} /> : null;
      case 'business_insights':
        return isFeatureEnabled('canAccessBusinessIntelligence') && businessInsights && businessInsights.length > 0 ? 
          <BusinessInsightsWidget insights={businessInsights} /> : null;
      case 'realtime_streams':
        return isFeatureEnabled('canAccessRealTimeData') && realtimeData && realtimeData.length > 0 ? 
          <RealtimeStreamsWidget data={realtimeData} /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {currentUser?.full_name || currentUser?.name || 'there'}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">Current Plan</p>
            <p className="font-medium text-gray-900 capitalize">{userPlan}</p>
          </div>
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
        </div>
      </div>

      {/* Data Widgets Section */}
      <div className="space-y-8">
        {/* Trending Now - Full Width */}
        <div className="w-full">
          {layout.widgets
            .filter(widgetType => widgetType === 'trend_overview')
            .map((widgetType) => {
              const widget = renderWidget(widgetType);
              return widget ? (
                <div key={widgetType} className="w-full">
                  {widget}
                </div>
              ) : null;
            })}
        </div>

        {/* Trend Overview Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trend Performance Overview</h3>
          <div className="h-64 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowTrendingUpIcon className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-gray-600">Interactive trend performance chart</p>
              <p className="text-sm text-gray-500 mt-2">Shows engagement trends over time</p>
            </div>
          </div>
        </div>

        {/* Other Data Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {layout.widgets
            .filter(widgetType => 
              ['quick_stats', 'recent_alerts', 'viral_predictions', 'momentum_analysis', 'business_insights', 'forecasting', 'audience_analysis', 'custom_reports', 'market_opportunities', 'realtime_streams', 'custom_metrics', 'model_performance', 'data_sources'].includes(widgetType)
            )
            .map((widgetType) => {
              const widget = renderWidget(widgetType);
              return widget ? (
                <div key={widgetType} className="col-span-1">
                  {widget}
                </div>
              ) : null;
            })}
        </div>

        {/* API Usage and Subscription Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {layout.widgets
            .filter(widgetType => 
              ['api_usage', 'subscription_management'].includes(widgetType)
            )
            .map((widgetType) => {
              const widget = renderWidget(widgetType);
              return widget ? (
                <div key={widgetType} className="col-span-1 flex">
                  <div className="w-full">
                    {widget}
                  </div>
                </div>
              ) : null;
            })}
        </div>
      </div>

      {/* Upgrade Prompt for lower tiers */}
      {userPlan === 'starter' && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Upgrade to Professional</h3>
              <p className="text-purple-100">Get access to viral predictions, momentum analysis, and advanced analytics</p>
            </div>
            <button className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Upgrade Now
            </button>
          </div>
        </div>
      )}
      
      {/* Upgrade Prompt for professional tier */}
      {userPlan === 'professional' && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Upgrade to Business</h3>
              <p className="text-blue-100">Get access to business intelligence, custom reports, and advanced forecasting</p>
            </div>
            <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Upgrade Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 